import { useAuthStore } from '@auth/authStore';
import { FileEvidence, ImageEvidence } from '@features/evidence';
import { getAnomalyReportFileName, historyAnomalyKey } from '@entities';
import HistoryLink from '@/features/lifecycle-history/HistoryLink';
import styles from './AnomalyDetail.module.css';

export const anomalyColumnsFull = [
  { label: "Anomaly Ref.", key: "anomaly_ref" },
  { label: "Site Name", key: "site_id" },
  { label: "Asset Name", key: "asset_id" },
  { label: "Task ID", key: "task_id" },
  { label: "Date Raised", key: "raised_at" },
  { label: "Failure Mode", key: "failure_mode" },
  { label: "Severity", key: "anomaly_severity" },
  { label: "Status", key: "anomaly_status" },
  { label: "Comments", key: "anomaly_comments" },
  { label: "Inspection Technique", key: "inspection_technique" },
  { label: "Inspected By", key: "inspected_by" },
  { label: "Component", key: "rds_component" },
  { label: "Description", key: "rds_description" },
  { label: "Subsystem", key: "rds_subsystem" },
  // { label: "Report ID", key: "anomaly_report_id" },
  { label: "Preceded By", key: "preceded_by" },
  { label: "Succeeded By", key: "succeeded_by" },
  { label: "Open?", key: "open_closed" },
];

const sections = [
  { title: 'Finding', keys: ['anomaly_ref', 'failure_mode', 'anomaly_severity', 'anomaly_status', 'open_closed', 'anomaly_comments'] },
  { title: 'Inspection', keys: ['task_id', 'raised_at', 'inspection_technique', 'inspected_by'] },
  { title: 'Component and location', keys: ['site_id', 'asset_id', 'rds_component', 'rds_description', 'rds_subsystem'] },
  { title: 'Record links', keys: ['preceded_by', 'succeeded_by'] },
];

function label(value) {
  if (Array.isArray(value)) return value.length ? value.join(' · ') : 'Not recorded';
  return value === null || value === undefined || value === '' ? 'Not recorded' : String(value);
}

export default function AnomalyDetail({ content, showHistoryLink = true }) {
  const companyId = useAuthStore(state => state.companyID);
  const reportFileName = getAnomalyReportFileName(content);
  const severity = String(content.anomaly_severity ?? '').trim().match(
    /^(?:(?:severity|level)\s+)?([1-5])$/i
  )?.[1];

  return (
    <div className={styles.container}>
      {showHistoryLink && companyId && (
        <div>
          <HistoryLink
            siteId={content.site_id}
            assetId={content.asset_id}
            recordId={historyAnomalyKey(content, companyId)}
          >
            View this record in history
          </HistoryLink>
        </div>
      )}
      <div className={styles.summary}>
        <div>
          <span className={styles.eyebrow}>FINDING CONTEXT</span>
          <h3>{label(content.failure_mode)}</h3>
          <p>{label(content.rds_component)} · {label(content.rds_description)}</p>
        </div>
        <div className={styles.badges}>
          <span className={styles.severity} data-level={severity || 'unknown'}>
            Severity: {label(content.anomaly_severity)}
          </span>
          <span className={styles.status}>Status: {label(content.anomaly_status)}</span>
        </div>
      </div>
      <div className={styles.layout}>
        <div className={styles.metadataColumn}>
          {sections.map(section => (
            <section key={section.title} className={styles.card}>
              <h3>{section.title}</h3>
              <dl className={styles.metadata}>
                {section.keys.map(key => {
                  const column = anomalyColumnsFull.find(item => item.key === key);
                  return <div key={key} className={key === 'anomaly_comments' ? styles.wide : undefined}>
                    <dt>{column?.label || key}</dt>
                    <dd>{label(content[key])}</dd>
                  </div>;
                })}
              </dl>
            </section>
          ))}
        </div>
        <div className={styles.evidenceColumn}>
          <section className={styles.card}>
            <h3>Report evidence</h3>
            <div className={styles.cardBody}>
              {reportFileName ? <>
                <p className={styles.reference}>{label(content.anomaly_report_id)}</p>
                <FileEvidence companyId={companyId} siteId={content.site_id} assetId={content.asset_id}
                  folder="reports" fileName={reportFileName} kind="report" />
              </> : <p className={styles.note}>No report is attached to this anomaly.</p>}
            </div>
          </section>
          <section className={styles.card}>
            <h3>Photo evidence</h3>
            <div className={styles.gallery}>
              <ImageEvidence companyId={companyId} siteId={content.site_id}
                assetId={content.asset_id} recordId={content.anomaly_ref}
                images={content.images} title="Attached images" />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}